/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Settings radio card
 *
 * @author Satyaki Bose
 * @file SettingsRadioCard.jsx
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Radio } from '@mui/material';
import { SettingsRadioCardStylesRoot, classes } from './styles/settingsRadioCardStyles';

const SettingsRadioCard = ({
  id,
  title,
  description,
  selected,
  disabled,
  onSelect,
  children
}) => {
  const handleSelect = () => {
    if (disabled) {
      return;
    }

    onSelect?.(id);
  };

  const cardClassName = [
    classes.card,
    selected ? classes.cardSelected : '',
    disabled ? classes.cardDisabled : ''
  ].filter(Boolean).join(' ');

  // native id/htmlFor pairing so clicking anywhere in the label (title, description)
  // toggles the underlying MUI Radio input, same as before with the whole card clickable
  const radioInputId = `settings-radio-card-${id}`;

  return (
    <SettingsRadioCardStylesRoot className={cardClassName}>
      <label htmlFor={radioInputId} className={classes.selectableRegion}>
        <Radio
          id={radioInputId}
          checked={selected}
          onChange={handleSelect}
          value={id}
          disabled={disabled}
          disableRipple
          className={classes.radio}
          focusVisibleClassName={classes.radioFocusVisible}
          icon={<span className={classes.radioOuter} />}
          checkedIcon={(
            <span className={`${classes.radioOuter} ${classes.radioOuterSelected}`}>
              <span className={classes.radioInner} />
            </span>
          )}
        />
        <span className={classes.main}>
          <p className={classes.title}>{title}</p>
          <p className={classes.description}>{description}</p>
        </span>
      </label>
      {children ? (
        <div className={classes.extraContent}>
          {children}
        </div>
      ) : null}
    </SettingsRadioCardStylesRoot>
  );
};

SettingsRadioCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onSelect: PropTypes.func,
  children: PropTypes.node
};

SettingsRadioCard.defaultProps = {
  selected: false,
  disabled: false,
  onSelect: null,
  children: null
};

export default SettingsRadioCard;
